import type { Notification } from '@ralph/contracts'

/**
 * Where a notification takes its reader (PRD-26 D-31, GRP-090: every kind has a destination).
 *
 * Mapped from the resource the server named, and the kind where one resource type serves two
 * readers. Each branch below states the reader it is for, because a destination is only right for
 * somebody: the maintainer told a lending decision was answered is the asker, and the maintainer
 * told somebody asked is the one who decides.
 *
 * `null` means there is nothing to open — a role granted or revoked is news about the reader
 * themselves — and the bell then only marks it read. An unknown resource type is `null` too: a
 * newer server may write kinds this client does not know, and guessing a route for one would take
 * somebody somewhere unrelated.
 */

type Destination = Pick<Notification, 'kind' | 'resourceType' | 'resourceId'>

/** Kinds about an agent that its maintainers act on in the builder. */
const BUILDER_KINDS: ReadonlySet<Notification['kind']> = new Set<Notification['kind']>([
  'sharing_decided',
  'health_changed',
  'suspended',
  'ownership_transferred',
  'editor_invited',
  'needs_owner'
])

const path = (id: string): string => encodeURIComponent(id)

export const notificationDestination = (notification: Destination): string | null => {
  const id = notification.resourceId
  if (!id) return null

  switch (notification.resourceType) {
    case 'agent_listing':
      // Maintainers and co-editors: a decision on their sharing request, health, a suspension, a
      // move or an invitation are all acted on in the builder. `lending_decided` goes to the
      // asker — a maker or a reader — and the agent's page is where both see its knowledge now.
      return BUILDER_KINDS.has(notification.kind) ? `/agents/${path(id)}/edit` : `/agents/${path(id)}`

    case 'agent':
      return `/agents/${path(id)}`

    case 'shared_question':
      // The agent's maintainers, to its activity: the questions people shared live there.
      return `/agents/mine?insights=${path(id)}`

    case 'sharing_request':
      // Administrators, to the card they decide on.
      return `/governance?view=sharing&request=${path(id)}`

    case 'knowledge_base':
      // `lending_requested` goes to the base's maintainers (or administrators, when nobody
      // maintains it), who answer it where its agents are listed; every other kind is about the
      // base itself — a decision on its sharing request, for instance.
      return notification.kind === 'lending_requested' ? `/knowledge/${path(id)}?tab=access` : `/knowledge/${path(id)}`

    case 'lending_request':
      // Only the request's id: the Knowledge page resolves it to its base.
      return `/knowledge?lending=${path(id)}`

    case 'group':
      // `shared_into_group`: that group's managers, to the group's page, which lists what is
      // shared with it.
      return `/groups/${path(id)}`

    default:
      // `user` (a role granted or revoked) and anything this client does not know.
      return null
  }
}
